import * as React from 'react';
import type { SubmitHandler } from 'react-hook-form';

import {
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogFooterSecondary,
    DialogHeader,
    FieldGroup,
    Form,
    Heading,
    Label,
    Submit,
    Switch,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient, useSiteContext } from '../../../../../state';
import {
    SiteRedirectFieldset,
    type SiteRedirectFormOutputs,
    useSiteRedirectForm,
} from './SiteRedirectForm';

export function AddRedirectDialog() {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Add redirect</Heading>
            </DialogHeader>
            <AddRedirectForm />
        </Dialog>
    );
}

function AddRedirectForm() {
    const { organizationId, site } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();
    const form = useSiteRedirectForm({ disabled: isPending });
    const [addMore, setAddMore] = React.useState(false);

    // For the first render, we use autoFocus to focus the source field.
    // but after the form is reset, we want to focus the source field again.
    useFocusFieldAfterReset(form);

    const onSubmit: SubmitHandler<SiteRedirectFormOutputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.siteRedirects.createSiteRedirect(organizationId, site.id, data);
                toast.success('Redirect added');
                if (!addMore) {
                    await close();
                } else {
                    form.reset({ source: '', destination: data.destination });
                }
            }, 'Error while adding redirect');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <SiteRedirectFieldset />
            </DialogBody>
            <DialogFooter>
                <DialogFooterSecondary>
                    <FieldGroup orientation="horizontal">
                        <Label headless>Add another redirect</Label>
                        <Switch value={addMore} onValueChange={setAddMore} disabled={isPending} />
                    </FieldGroup>
                </DialogFooterSecondary>
                <DialogCloseButton />
                <Submit label="Add" />
            </DialogFooter>
        </Form>
    );
}

function useFocusFieldAfterReset(form: ReturnType<typeof useSiteRedirectForm>) {
    const { isDirty, isSubmitted, disabled } = form.formState;
    const { setFocus } = form;
    React.useLayoutEffect(() => {
        if (!isDirty && isSubmitted && !disabled) {
            setFocus('source');
        }
    }, [isDirty, isSubmitted, disabled, setFocus]);
}
